import type { QueryClient } from '@tanstack/react-query';

/**
 * Every React Query key that renders a view of `time_off_requests`. THIS LIST
 * IS THE SINGLE SOURCE: a screen that adds a view of the table adds its key
 * here, and every mutation of the table — office or field — calls
 * `invalidateTimeOffQueries`.
 *
 *   ['dispatch-time-off']         TimeOffScreen's Pending/Approved/Rejected tabs
 *                                 — GET /dispatch/time-off
 *   ['time-off-calendar']         TimeOffScreen's Calendar tab
 *                                 — GET /dispatch/time-off/calendar
 *   ['home-timeoff']              HomeScreen's "N Time-Off Requests Pending
 *                                 Approval" banner — GET /reports/dashboard/time-off
 *   ['time-off-dashboard']        TeamScreen's "currently on days off" dots — the
 *                                 SAME endpoint as the banner under a DIFFERENT key
 *   ['dispatch-calendar-timeoff'] pages/DispatchCalendar's time-off strip — the
 *                                 same endpoint as the Calendar tab, another key
 *   ['field-time-off']            the field hand's own request list
 *   ['field-time-off-overlaps']   TimeOffOverlaps — who else is off on the dates
 *                                 being requested
 *   ['field-time-off-upcoming']   UpcomingCrewDaysOff — the crew's approved days
 *                                 off on the field screen
 *
 * One list because each mutation used to name its own subset and the subsets
 * were wrong in different directions: the office screen missed the dispatch
 * calendar and every field view, and the field screen invalidated only its own
 * list, so a request filed there left every office view stale. A per-screen key
 * naming a SHARED endpoint has no way to announce itself to the mutation
 * that should refresh it; this list is how it does. (`home-timeoff` went five
 * months invalidated by nothing at all; `refetchOnWindowFocus: false` in
 * App.tsx is why a missed key stays stale rather than healing on focus.)
 *
 * SAME-TAB ONLY. Invalidation reaches the QueryClient of the tab that made the
 * change. A request filed on a phone does not refresh a dispatcher's open
 * laptop tab — that needs a refetch-on-focus or staleTime policy, which this
 * does not provide.
 */
export const TIME_OFF_QUERY_KEYS = [
  'dispatch-time-off',
  'time-off-calendar',
  'home-timeoff',
  'time-off-dashboard',
  'dispatch-calendar-timeoff',
  'field-time-off',
  'field-time-off-overlaps',
  'field-time-off-upcoming',
] as const;

/** Refresh every view of `time_off_requests`. @see TIME_OFF_QUERY_KEYS. */
export function invalidateTimeOffQueries(queryClient: QueryClient): void {
  for (const key of TIME_OFF_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}
